
import { useEffect, useCallback } from "react";

interface UseExploreEffectsProps {
  categories: any[];
  initialCategoriesLoaded: React.MutableRefObject<boolean>;
  setCategoriesLoaded: (loaded: boolean) => void;
  categoryIdFromSlug: string | null;
  setSelectedCategory: (id: string | null) => void;
  searchQuery: string;
  selectedCategory: string | null;
  selectedSort: string;
  selectedTimeFilter: string;
  handleEnhancedSearchChange: (query: string) => void;
  handleSearchFilterChange: (filters: any) => void;
  isCategoryProcessing: React.MutableRefObject<boolean>;
  loadPrompts: () => void;
  loadDebounceTimer: React.MutableRefObject<NodeJS.Timeout | null>;
  lastDepsString: React.MutableRefObject<string>;
  initialLoadAttempted: React.MutableRefObject<boolean>;
  manualLoadTriggered: React.MutableRefObject<boolean>;
  currentPage: number;
  isInitialized: boolean;
  setIsInitialized: (initialized: boolean) => void;
  categoryChangeTimeout: React.MutableRefObject<NodeJS.Timeout | null>;
}

export function useExploreEffects({
  categories,
  initialCategoriesLoaded,
  setCategoriesLoaded,
  categoryIdFromSlug,
  setSelectedCategory,
  searchQuery,
  selectedCategory,
  selectedSort,
  selectedTimeFilter,
  handleEnhancedSearchChange,
  handleSearchFilterChange,
  isCategoryProcessing,
  loadPrompts,
  loadDebounceTimer,
  lastDepsString,
  initialLoadAttempted,
  manualLoadTriggered,
  currentPage,
  isInitialized,
  setIsInitialized,
  categoryChangeTimeout
}: UseExploreEffectsProps) {
  // Update categoriesLoaded flag when categories are loaded
  useEffect(() => {
    if (initialCategoriesLoaded.current && categories.length > 0) {
      console.log("Categories loaded successfully:", categories.length);
      setCategoriesLoaded(true);
    }
  }, [categories, initialCategoriesLoaded.current, setCategoriesLoaded]);

  // Set initial category when categoryIdFromSlug changes
  useEffect(() => {
    if (categoryIdFromSlug && initialCategoriesLoaded.current) {
      console.log(`Setting initial category ID: ${categoryIdFromSlug}`);
      setSelectedCategory(categoryIdFromSlug);
    }
  }, [categoryIdFromSlug, setSelectedCategory, initialCategoriesLoaded.current]);

  // Sync enhanced search with current filters
  useEffect(() => {
    if (searchQuery) {
      console.log("Updating search with filters:", { selectedSort, selectedTimeFilter });
      handleEnhancedSearchChange(searchQuery);
      handleSearchFilterChange({ 
        categoryFilter: selectedCategory || "",
        sortBy: selectedSort as any,
        timeFilter: selectedTimeFilter
      });
    }
  }, [searchQuery, selectedCategory, selectedSort, selectedTimeFilter, handleEnhancedSearchChange, handleSearchFilterChange]);

  // Improved, more resilient debounced loadPrompts function
  const debouncedLoadPrompts = useCallback(() => {
    // Don't load regular prompts if we have a search query
    if (searchQuery) {
      return;
    }
    
    // Clear previous timer
    if (loadDebounceTimer.current) {
      clearTimeout(loadDebounceTimer.current);
    }
    
    // Set new timer
    loadDebounceTimer.current = setTimeout(() => {
      console.log("Executing debounced loadPrompts with sort:", selectedSort, "timeFilter:", selectedTimeFilter);
      loadPrompts();
    }, 600);
  }, [loadPrompts, searchQuery, selectedSort, selectedTimeFilter, loadDebounceTimer]);

  // Force initial load on direct page access
  useEffect(() => {
    if (initialCategoriesLoaded.current && !manualLoadTriggered.current && !searchQuery) {
      console.log("Triggering initial prompt load on page access");
      manualLoadTriggered.current = true;
      
      setTimeout(() => {
        loadPrompts();
      }, 100);
    }
  }, [initialCategoriesLoaded.current, loadPrompts, searchQuery, manualLoadTriggered]);

  // Load prompts when dependencies change (only for non-search scenarios)
  useEffect(() => {
    // Skip if we have a search query (handled by enhanced search)
    if (searchQuery) {
      return;
    }
    
    // Only attempt to load prompts if categories have been loaded
    if (!initialCategoriesLoaded.current || isCategoryProcessing.current) {
      return;
    }
    
    // Create a dependency string to compare against the last one
    const depsString = `${selectedCategory}-${currentPage}-${selectedSort}-${selectedTimeFilter}`;
    
    // Only trigger a reload if the dependencies have actually changed
    if (depsString !== lastDepsString.current || !initialLoadAttempted.current) {
      console.log(`Triggering loadPrompts due to dependency changes: ${depsString}`);
      lastDepsString.current = depsString;
      
      debouncedLoadPrompts();
      initialLoadAttempted.current = true;
      
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [loadPrompts, selectedCategory, currentPage, searchQuery, selectedSort, selectedTimeFilter, isInitialized, debouncedLoadPrompts, isCategoryProcessing, initialCategoriesLoaded, lastDepsString, initialLoadAttempted, setIsInitialized]);

  // Handle initialization timeout
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isInitialized && categories.length > 0) {
        console.log("Forcing initialization after timeout");
        setIsInitialized(true);
      }
    }, 3000);
    
    return () => clearTimeout(timeoutId);
  }, [categories, isInitialized, setIsInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (categoryChangeTimeout.current) {
        clearTimeout(categoryChangeTimeout.current);
      }
      
      if (loadDebounceTimer.current) {
        clearTimeout(loadDebounceTimer.current);
      }
    };
  }, [categoryChangeTimeout, loadDebounceTimer]);
}
